<template>
  <div class="ls-text">
    <label
      class="ls-text__label"
      :for="uid"
    >{{ label }}</label>
    <textarea
      :id="uid"
      ref="area"
      :value="draft"
      class="ls-text__area"
      :rows="rows"
      :aria-describedby="`${uid}-meta`"
      @input="onInput($event.target.value)"
      @focus="editing = true"
      @blur="onBlur"
    />
    <p
      :id="`${uid}-meta`"
      class="ls-text__meta"
    >
      <span
        class="ls-text__status"
        data-test="ls-text-status"
        aria-live="polite"
      >{{ touched ? statusLabel : '' }}</span>
      <span
        v-if="showCounter"
        class="ls-text__counter"
        data-test="ls-text-counter"
      >{{ length }} of {{ limit }}</span>
    </p>
  </div>
</template>

<script>
import { fitInsertion, runeCount } from './feedbackText'
import { saveStatusLabel } from './feedbackStatus'

// The counter appears in the last tenth of the allowance.
const COUNTER_FROM = 0.9

let seq = 0

/**
 * A note field on the branded line sheet (KP-4572). Keeps its own draft
 * while the buyer types, so a save coming back never moves the cursor, and
 * reports each change (`input`) and the end of editing (`commit`, on blur).
 */
export default {
  name: 'LsFeedbackText',
  props: {
    label: { type: String, required: true },
    value: { type: String, default: '' },
    limit: { type: Number, required: true },
    status: { type: String, default: 'idle' },
    rows: { type: Number, default: 3 },
  },
  data() {
    seq += 1
    return {
      uid: `ls-text-${seq}`, draft: this.value, editing: false, touched: false,
    }
  },
  computed: {
    length() {
      return runeCount(this.draft)
    },
    showCounter() {
      return this.length >= Math.floor(this.limit * COUNTER_FROM)
    },
    statusLabel() {
      return saveStatusLabel(this.status)
    },
  },
  watch: {
    value(next) {
      if (!this.editing) this.draft = next
    },
  },
  methods: {
    onInput(text) {
      // Cut by code points like the counter and the server (maxlength counts
      // UTF-16 units and would stop emoji at half the allowance), and only
      // the part just typed or pasted, so the note already there survives.
      const area = this.$refs.area
      const fit = fitInsertion(this.draft, text, area ? area.selectionEnd : text.length, this.limit)
      if (fit.text !== text && area) {
        area.value = fit.text
        area.setSelectionRange(fit.cursor, fit.cursor)
      }
      this.draft = fit.text
      this.touched = true
      this.$emit('input', this.draft)
    },
    onBlur() {
      this.editing = false
      if (this.touched) this.$emit('commit')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-text {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    @include ls-label;
    color: var(--ls-ink);
  }

  &__area {
    width: 100%;
    min-height: 88px;
    padding: 12px 14px;
    border: 1px solid var(--ls-line);
    border-radius: var(--ls-radius);
    background: var(--ls-ground);
    color: var(--ls-ink);
    font: 400 1rem/1.5 var(--ls-body-font);
    resize: vertical;

    &:focus {
      outline: 2px solid var(--ls-ink);
      outline-offset: 1px;
      border-color: var(--ls-ink);
    }
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    min-height: 1.25rem;
    margin: 0;
    color: var(--ls-muted);
    font: 400 0.8125rem/1.4 var(--ls-body-font);
  }

  &__counter {
    margin-left: auto;
    font-variant-numeric: tabular-nums;
  }
}
</style>
